import type { ErrorCode } from '@avrahami/contracts';
import { Alert, Button, Card, Cell, ConfirmDialog, isApiError, Loading, Table } from '@avrahami/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { he } from '../i18n/he';
import { errorMessage, NO_ERRORS, type FieldErrors, type Parsed } from '../lib/forms';
import { FormStatus, mergeErrors } from './fields';

/** What a save does once the draft passed the contract: or the field errors it did not. */
export type Submission =
  { ok: true; run: () => Promise<unknown> } | { ok: false; errors: FieldErrors };

/** Binds a checked request to the call that sends it. */
export function submitWith<Value>(
  parsed: Parsed<Value>,
  call: (value: Value) => Promise<unknown>,
): Submission {
  return parsed.ok ? { ok: true, run: () => call(parsed.value) } : parsed;
}

export interface EntityFormProps<Item, Draft> {
  draft: Draft;
  /** Merges a change into the draft. */
  set: (change: Partial<Draft>) => void;
  errors: FieldErrors;
  /** The entity being edited; undefined for a new one. */
  item: Item | undefined;
}

/** The entity being edited is kept as it was opened: its version is the one the save sends. */
type Editing<Item> = { mode: 'new' } | { mode: 'edit'; item: Item } | null;

/**
 * The list-and-form pattern of the small store entities (categories, popups, slides): a table, a "new" button, one form at a time above the table, and delete with a
 * confirmation. Everything entity-specific comes in through the props; the API decides.
 */
export function CrudSection<Item extends { id: string }, Draft>({
  title,
  intro,
  entity,
  createLabel,
  newTitle,
  editTitle,
  emptyText,
  items,
  loading,
  loadError,
  invalidate,
  canWrite,
  maxItems,
  fullText,
  head,
  cells,
  rowData,
  draftOf,
  renderForm,
  submit,
  remove,
  canDelete = () => true,
  deleteOverrides,
  createConflict,
}: {
  title: string;
  intro?: string;
  /** The test hook prefix: `<entity>-row`, `<entity>-form`. */
  entity: string;
  createLabel: string;
  newTitle: string;
  editTitle: string;
  emptyText: string;
  items: readonly Item[] | undefined;
  loading: boolean;
  loadError: Error | null;
  invalidate: readonly (readonly unknown[])[];
  canWrite: boolean;
  maxItems?: number;
  fullText?: string;
  head: readonly string[];
  cells: (item: Item) => ReactNode;
  /** Extra data attributes of a row (the E2E finds rows by them, e.g. `data-code`). */
  rowData: (item: Item) => Readonly<Record<`data-${string}`, string>>;
  draftOf: (item: Item | undefined) => Draft;
  renderForm: (props: EntityFormProps<Item, Draft>) => ReactNode;
  submit: (draft: Draft, item: Item | undefined) => Submission;
  remove: (item: Item) => Promise<unknown>;
  /** Rows that offer "delete" (all by default). */
  canDelete?: (item: Item) => boolean;
  /** What an API code means when deleting here (a category that is not empty answers CONFLICT). */
  deleteOverrides?: Partial<Readonly<Record<ErrorCode, string>>>;
  /** What CONFLICT means when creating (the store reached the most it may have). */
  createConflict?: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing<Item>>(null);
  const [draft, setDraft] = useState<Draft>();
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const [deleting, setDeleting] = useState<Item>();

  const refresh = () =>
    Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })));

  const save = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setEditing(null);
      setDraft(undefined);
      await refresh();
    },
    // A stale version (changed elsewhere) or a full list: show the list as it is now.
    onError: async (error) => {
      if (isApiError(error, 'CONFLICT')) await refresh();
    },
  });
  const del = useMutation({
    mutationFn: (item: Item) => remove(item),
    onSuccess: async () => {
      setDeleting(undefined);
      await refresh();
    },
  });

  const editedItem = editing?.mode === 'edit' ? editing.item : undefined;

  const open = (next: Editing<Item>, item: Item | undefined) => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    setEditing(next);
    setDraft(draftOf(item));
  };
  /** After a CONFLICT: edit the entity as the server has it now (or close when it is gone). */
  const reloadEdited = () => {
    if (editing?.mode !== 'edit') return;
    const fresh = items?.find((item) => item.id === editing.item.id);
    if (fresh === undefined) {
      close();
      return;
    }
    open({ mode: 'edit', item: fresh }, fresh);
  };
  const close = () => {
    setEditing(null);
    setDraft(undefined);
  };

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (draft === undefined) return;
    const submission = submit(draft, editedItem);
    if (!submission.ok) {
      setLocalErrors(submission.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(submission.run);
  };

  const full = maxItems !== undefined && (items?.length ?? 0) >= maxItems;

  return (
    <div className="flex flex-col gap-6">
      {editing !== null && draft !== undefined ? (
        <Card title={editing.mode === 'new' ? newTitle : editTitle}>
          <form
            onSubmit={onSubmit}
            noValidate
            className="flex flex-col gap-4"
            data-testid={`${entity}-form`}
          >
            {renderForm({
              draft,
              set: (change) => {
                setDraft((current) =>
                  current === undefined ? current : { ...current, ...change },
                );
              },
              errors: mergeErrors(localErrors, save.error),
              item: editedItem,
            })}
            <FormStatus
              error={save.error}
              localInvalid={Object.keys(localErrors).length > 0}
              {...(editing.mode === 'new' && createConflict !== undefined
                ? { overrides: { CONFLICT: createConflict } }
                : {})}
              {...(editing.mode === 'edit' ? { onReload: reloadEdited } : {})}
            />
            <div className="flex gap-2">
              <Button type="submit" busy={save.isPending}>
                {he.common.save}
              </Button>
              <Button tone="secondary" onClick={close} disabled={save.isPending}>
                {he.common.cancel}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card
        title={title}
        actions={
          canWrite && editing === null ? (
            <Button
              disabled={full}
              onClick={() => {
                open({ mode: 'new' }, undefined);
              }}
            >
              {createLabel}
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-4">
          {intro === undefined ? null : <p className="text-sm text-slate-600">{intro}</p>}
          {full && canWrite && fullText !== undefined ? (
            <p className="text-xs text-slate-500">{fullText}</p>
          ) : null}
          {loading ? <Loading /> : null}
          {loadError === null ? null : <Alert>{errorMessage(loadError)}</Alert>}
          {items?.length === 0 ? <p className="text-sm text-slate-500">{emptyText}</p> : null}
          {items !== undefined && items.length > 0 ? (
            <Table head={canWrite ? [...head, he.common.actions] : head}>
              {items.map((item) => (
                <tr key={item.id} data-testid={`${entity}-row`} {...rowData(item)}>
                  {cells(item)}
                  {canWrite ? (
                    <Cell className="whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          tone="secondary"
                          onClick={() => {
                            open({ mode: 'edit', item }, item);
                          }}
                        >
                          {he.common.edit}
                        </Button>
                        {canDelete(item) ? (
                          <Button
                            tone="secondary"
                            onClick={() => {
                              del.reset();
                              setDeleting(item);
                            }}
                          >
                            {he.common.delete}
                          </Button>
                        ) : null}
                      </div>
                    </Cell>
                  ) : null}
                </tr>
              ))}
            </Table>
          ) : null}
        </div>
      </Card>

      <ConfirmDialog
        open={deleting !== undefined}
        title={he.common.deleteTitle}
        confirmLabel={he.common.delete}
        tone="danger"
        busy={del.isPending}
        onConfirm={() => {
          if (deleting !== undefined) del.mutate(deleting);
        }}
        onClose={() => {
          setDeleting(undefined);
        }}
      >
        <p>{he.common.deleteBody}</p>
        {del.isError ? (
          <div className="mt-3">
            <Alert>{errorMessage(del.error, deleteOverrides)}</Alert>
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
